import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Clock, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { journeySignInPath } from '@/lib/auth/post-auth-landing';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { OTP_UI } from '@/lib/constants/otp';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { TWO_FACTOR_METHODS, type TwoFactorMethod } from '@/lib/constants/two-factor';
import { getPendingLogin } from '@/lib/auth/pre-auth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { OtpInput } from '@/components/ui/OtpInput';
import { Input } from '@/components/ui/Input';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { useVerifyTwoFactor } from '@/hooks/useVerifyTwoFactor';
import { useResendTwoFactor } from '@/hooks/useResendTwoFactor';
import { useVerifyRecoveryCode } from '@/hooks/useVerifyRecoveryCode';
import {
  RECOVERY_SIGN_IN_STRINGS as RS,
  TWO_FACTOR_SETUP_STRINGS,
} from '@/lib/constants/two-factor-strings';
import { useCountdown } from '@/hooks/useCountdown';
import { getApiErrorCode, getApiErrorMessage, mapApiError } from '@/lib/utils/error.utils';

const COPY = UI_STRINGS.TWO_FACTOR;

const VERIFY_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.OTP_INVALID]: COPY.ERROR_INVALID,
  [API_ERROR_CODES.OTP_NOT_FOUND]: COPY.ERROR_INVALID,
  [API_ERROR_CODES.OTP_EXPIRED]: COPY.ERROR_EXPIRED,
  [API_ERROR_CODES.OTP_LOCKED]: COPY.ERROR_LOCKED,
  // Authenticator app and recovery codes (MYN-241).
  [API_ERROR_CODES.TOTP_CODE_INVALID]: TWO_FACTOR_SETUP_STRINGS.ERROR_CODE_INVALID,
  [API_ERROR_CODES.MFA_SESSION_EXPIRED]: TWO_FACTOR_SETUP_STRINGS.ERROR_SESSION_EXPIRED,
  [API_ERROR_CODES.RECOVERY_CODE_INVALID]: RS.ERROR_RECOVERY_INVALID,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: TWO_FACTOR_SETUP_STRINGS.ERROR_LOCKED,
};

const RESEND_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.RATE_LIMITED]: COPY.ERROR_RATE_LIMITED,
};

/** Codes after which waiting out the cooldown makes no sense - resend now. */
const RESEND_UNLOCK_CODES = new Set<string>([
  API_ERROR_CODES.OTP_EXPIRED,
  API_ERROR_CODES.OTP_LOCKED,
]);

/** Which code the user is typing: the app/email code, or a recovery code (MYN-241). */
type CodeMode = 'code' | 'recovery';

/** Everything that varies by code mode, resolved once per render. */
interface CodeModeConfig {
  mutation: { isPending: boolean; isError: boolean; error: unknown };
  cardSubtitle: string;
  toggleLabel: string;
  next: CodeMode;
  field: ReactNode;
  /** Validates the typed value; returns an error message or submits. */
  submit: () => string | undefined;
}

interface TwoFactorPresentation {
  /**
   * Whether this factor has a delivery step. Gates the "code sent to" banner,
   * the expiry countdown, and the resend control together - a resend button on
   * a factor that was never delivered is a defect (§11 decision 4).
   */
  hasDelivery: boolean;
  cardSubtitle: string;
  subtitle: ReactNode;
}

/**
 * Everything that varies by second-factor method, resolved once. Reading the
 * fields off one result keeps the case list from drifting the way separate
 * `method === ...` tests in each prop would.
 */
function getPresentation(method: TwoFactorMethod, hint: string | undefined): TwoFactorPresentation {
  switch (method) {
    case TWO_FACTOR_METHODS.EMAIL_OTP:
      return {
        hasDelivery: true,
        cardSubtitle: COPY.EMAIL_OTP_CARD_SUBTITLE,
        subtitle: (
          <>
            <p>
              {COPY.EMAIL_OTP_SUBTITLE_PREFIX}{' '}
              <span className="text-navy font-semibold whitespace-nowrap">{hint}</span>
            </p>
            <p className="mt-1 text-sm">{COPY.EMAIL_OTP_SUBTITLE_LINE2}</p>
          </>
        ),
      };
    case TWO_FACTOR_METHODS.TOTP:
    default:
      // `default` is unreachable under the pinned contract, but a future
      // method must fall on the no-delivery side: offering a resend for
      // something that was never sent is worse than omitting one.
      return {
        hasDelivery: false,
        cardSubtitle: COPY.TOTP_CARD_SUBTITLE,
        subtitle: <p>{COPY.TOTP_SUBTITLE}</p>,
      };
  }
}

/**
 * Step 2 of login (FR-03/FR-04, MYN-139): collect the second factor and
 * exchange it for the session.
 *
 * Composed entirely from existing primitives (`OtpInput`, `CountdownTimer`,
 * `useCountdown`) and structured after `InviteVerifyOtpPage`. Reachable only
 * as a transition from `LoginPage` - it fails ADR FE-1's "can they arrive
 * cold?" test, so arriving without a pre-auth credential redirects to login
 * rather than rendering an unusable form.
 */
export function TwoFactorPage() {
  // Read once per mount (non-destructive, so StrictMode's dev-only
  // double-mount still finds it - see `getPendingLogin`'s doc comment). Not on
  // every render: a successful verify clears the holder before its navigate
  // lands, and a re-render in between read the empty holder and redirected a
  // signed-in user back to /login (MYN-272, staging on Firefox). A fresh mount
  // with nothing pending (reload, back button) still redirects.
  const [pending] = useState(getPendingLogin);
  const challenge = pending?.challenge;
  const email = pending?.email ?? '';

  const preAuthToken = challenge?.preAuthToken ?? '';
  const verify = useVerifyTwoFactor(preAuthToken, pending?.returnTo);
  const recovery = useVerifyRecoveryCode(preAuthToken, pending?.returnTo);
  const resend = useResendTwoFactor(preAuthToken);

  // Recovery-code sign-in replaces the app code for a lost phone (MYN-241).
  const [mode, setMode] = useState<CodeMode>('code');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  // Remounts the expiry countdown when a new code is sent.
  const [timerKey, setTimerKey] = useState(0);
  const cooldown = useCountdown(OTP_UI.RESEND_COOLDOWN_SECONDS);

  const presentation = getPresentation(
    challenge?.method ?? TWO_FACTOR_METHODS.EMAIL_OTP,
    challenge?.hint ?? email,
  );

  const modes: Record<CodeMode, CodeModeConfig> = {
    code: {
      mutation: verify,
      cardSubtitle: presentation.cardSubtitle,
      toggleLabel: RS.USE_RECOVERY,
      next: 'recovery',
      field: (
        <OtpInput
          label={COPY.CODE_LABEL}
          value={code}
          onChange={setCode}
          length={OTP_UI.CODE_LENGTH}
          error={codeError}
        />
      ),
      submit: () => {
        if (!new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`).test(code)) {
          return `Enter the ${OTP_UI.CODE_LENGTH}-digit code`;
        }
        verify.mutate({ code });
        return undefined;
      },
    },
    recovery: {
      mutation: recovery,
      cardSubtitle: RS.RECOVERY_CARD_SUBTITLE,
      toggleLabel: RS.USE_APP,
      next: 'code',
      field: (
        <Input
          label={RS.RECOVERY_LABEL}
          showLabel
          autoComplete="one-time-code"
          placeholder={RS.RECOVERY_PLACEHOLDER}
          value={recoveryCode}
          onChange={(event) => setRecoveryCode(event.target.value)}
          error={codeError}
          className="max-w-xs text-center font-mono tracking-wider"
        />
      ),
      submit: () => {
        if (!recoveryCode.trim()) return RS.RECOVERY_REQUIRED;
        recovery.mutate(recoveryCode.trim());
        return undefined;
      },
    },
  };
  const current = modes[mode];
  const active = current.mutation;

  const verifyErrorCode = getApiErrorCode(active.error);
  const resendReady =
    cooldown.expired || (verifyErrorCode !== undefined && RESEND_UNLOCK_CODES.has(verifyErrorCode));

  // Prefer the backend's own detail - that is where a remaining-attempts count
  // arrives ("Incorrect code. 3 attempts remaining."). Fall back to code-mapped
  // copy, then a generic message. Credential-stage failures are never
  // elaborated here; that is step 1's concern (FR-83).
  const verifyErrorMessage = active.isError
    ? getApiErrorMessage(active.error, mapApiError(active.error, VERIFY_ERROR_BY_CODE, COPY.ERROR))
    : undefined;

  // No pending login in memory: either step 1 was skipped, or a reload cleared
  // the module, or this step was already claimed and the user navigated back.
  // All three should start over.
  if (!challenge?.preAuthToken) {
    return <Navigate to={journeySignInPath()} replace />;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setCodeError(current.submit());
  };

  const handleResend = () => {
    // Cooldown/timer only restart once the resend actually succeeded - a
    // failed resend must not silently re-lock the button.
    resend.mutate(undefined, {
      onSuccess: () => {
        cooldown.reset();
        setTimerKey((key) => key + 1);
      },
    });
  };

  return (
    <div className="bg-page-bg flex flex-col items-center px-4 py-16">
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={COPY.BADGE}
        heading={
          <>
            {COPY.HEADING_PREFIX}
            <span className="text-green">{COPY.HEADING_HIGHLIGHT}</span>
          </>
        }
        subtitleClassName="max-w-2xl"
        subtitle={presentation.subtitle}
      />

      <div className="mt-10 flex w-full max-w-3xl flex-col gap-6">
        {presentation.hasDelivery && (
          <div className="rounded-card border-green/30 bg-green/10 flex items-center justify-between gap-4 border px-6 py-4">
            <div className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="bg-green/20 text-dkgreen flex h-10 w-10 items-center justify-center rounded-xl"
              >
                <Mail size={18} />
              </span>
              <div>
                <p className="text-dkgreen font-sans text-[11px] font-semibold tracking-wide uppercase">
                  {COPY.SENT_TO_LABEL}
                </p>
                <p className="text-navy font-sans text-sm font-semibold">
                  {challenge.hint ?? email}
                </p>
                <p className="text-subtext text-xs">{COPY.SENT_TO_HINT}</p>
              </div>
            </div>
            <Badge variant="green-outline">&bull; {COPY.SENT_CHIP}</Badge>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          <SectionCard
            title={COPY.CARD_TITLE}
            subtitle={current.cardSubtitle}
            icon={<KeyRound size={20} />}
            className={presentation.hasDelivery ? 'pb-0' : undefined}
          >
            {verifyErrorMessage && (
              <p role="alert" className="text-danger mb-4 text-center text-sm">
                {verifyErrorMessage}
              </p>
            )}
            {resend.isSuccess && (
              <p role="status" className="text-dkgreen mb-4 text-center text-sm">
                {COPY.RESENT}
              </p>
            )}
            {resend.isError && (
              <p role="alert" className="text-danger mb-4 text-center text-sm">
                {mapApiError(resend.error, RESEND_ERROR_BY_CODE, COPY.RESEND_ERROR)}
              </p>
            )}

            <div className="flex flex-col items-center gap-4 py-4">
              {current.field}
              {/* Recovery codes exist only for the authenticator app, which has no resend. */}
              {!presentation.hasDelivery && (
                <Button
                  type="button"
                  variant="link"
                  className="text-sm"
                  onClick={() => {
                    setMode(current.next);
                    setCodeError(undefined);
                    verify.reset();
                    recovery.reset();
                  }}
                >
                  {current.toggleLabel}
                </Button>
              )}
              {presentation.hasDelivery && (
                <p className="text-slate flex items-center gap-1.5 text-xs">
                  <Lock size={12} aria-hidden="true" />
                  <span>
                    {COPY.EXPIRES_NOTE_PREFIX}
                    <span className="text-navy font-semibold">{COPY.EXPIRES_NOTE_BOLD}</span>
                    {COPY.EXPIRES_NOTE_SUFFIX}
                  </span>
                </p>
              )}
            </div>

            {/* Expiry countdown and resend belong to a delivery step only. */}
            {presentation.hasDelivery && (
              <div className="rounded-b-card border-divider bg-page-bg -mx-8 mt-4 flex items-center justify-between gap-4 border-t px-8 py-4">
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="border-dkgreen text-dkgreen flex h-9 w-9 items-center justify-center rounded-full border-2"
                  >
                    <Clock size={16} />
                  </span>
                  <div className="leading-tight">
                    <p className="text-slate text-xs">{COPY.EXPIRES_IN}</p>
                    <CountdownTimer key={timerKey} seconds={OTP_UI.CODE_TTL_SECONDS} />
                  </div>
                </div>
                <p className="text-slate text-sm">
                  {COPY.NO_CODE}{' '}
                  <Button
                    type="button"
                    variant="link"
                    disabled={!resendReady || resend.isPending}
                    onClick={handleResend}
                    className="disabled:text-slate align-baseline"
                  >
                    {resend.isPending ? COPY.RESENDING : COPY.RESEND}
                  </Button>
                </p>
              </div>
            )}
          </SectionCard>

          <SectionCard>
            <ShieldNote className="justify-end">{COPY.ENCRYPTION_NOTE}</ShieldNote>
            <div className="mt-4 flex items-center justify-end gap-6">
              <Link
                to={journeySignInPath()}
                className="text-navy hover:text-dkgreen inline-flex items-center gap-1.5 font-sans text-sm font-medium"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                {COPY.BACK}
              </Link>
              <Button
                type="submit"
                variant="navy"
                disabled={active.isPending}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
              >
                {active.isPending ? COPY.SUBMITTING : COPY.SUBMIT}
                <ArrowRight size={18} aria-hidden="true" />
              </Button>
            </div>
          </SectionCard>
        </form>
      </div>
    </div>
  );
}
